import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState
} from 'react';
import Disclaimer, {
  DisclaimerType
} from '../components/disclaimer/Disclaimer';
import NoneLayout from '../components/common/NoneLayout';
import { useIdentityService } from './IdentityProvider';
import Walkthrough from '../components/walkthrough/Walkthrough';
import { useKinesis } from './KinesisProvider';
import { Company } from '../models/company.model';
import { useInternalSession } from './SessionProvider';
import { ssRead, ssWrite } from '../utils/session-storage.utils';

export const AgreementProvider: FC<PropsWithChildren> = ({ children }) => {
  const { identityClient } = useIdentityService();
  const { data: session }: any = useInternalSession();
  const { record } = useKinesis();

  const [state, setState] = useState<
    'settings' | 'disclaimer' | 'loading' | 'done'
  >('loading');
  const [disclaimerType, setType] = useState<DisclaimerType>();

  useEffect(() => {
    if (identityClient.isAdmin) {
      identityClient.getCompanyData().then((company) => {
        // 各タブにて、`session.provider`使ったウォークスルー遷移判定を一回だけ実行したい
        const firstAccessAfterSso = !ssRead('sso_provider');
        // テストユーザ（固定の親会員）がパスワードログインした場合は、SSOログインとして振る舞う
        const currentSsoProvider =
          company.companyId === 'test_kai_link_no' ? 'sso' : session?.provider;
        if (firstAccessAfterSso) {
          if (currentSsoProvider === 'sso') {
            ssWrite('sso_provider', currentSsoProvider);
          } else {
            throw Error(`unexpected session provider: ${currentSsoProvider}`);
          }
        }

        // SessionStorageに保存したproviderで遷移判定を実施
        let ssoProvider = ssRead('sso_provider');
        if (!ssoProvider) {
          console.warn('SessionStorage not available');
          ssoProvider = currentSsoProvider; // SessionStorageが使えない場合、元の通り動作
        }
        const disclaimerType = getDisclaimerType(company, ssoProvider);

        // 遷移判定
        if (disclaimerType) {
          setState('disclaimer');
          setType(disclaimerType);
        } else if (company.isEnableLine) {
          setState('done');
          if (ssoProvider !== 'done') ssWrite('sso_provider', 'done');
        }
      });
    } else {
      // メールログイン
      setState('done');
    }
  }, []);

  /** サービス利用開始状況に基づき表示すべき「利用規約」の種類を返却 */
  const getDisclaimerType = (
    company: Company,
    sessionProvider: 'sso' | 'done'
  ): DisclaimerType | null => {
    if (sessionProvider === 'sso') {
      return company.isEnableLine ? null : 'line';
    } else {
      return null;
    }
  };

  const disclaimerComponent = useMemo(() => {
    switch (state) {
      case 'disclaimer':
        return (
          <Disclaimer
            type={disclaimerType}
            onSubmit={() => setState('settings')}
          />
        );
      case 'settings':
        return (
          disclaimerType === 'line' && (
            <Walkthrough
              onClose={() => setState('done')}
              onEndStep={(flag) => {
                const date = Date.now();
                record({
                  name: 'INIT',
                  customDimention: {
                    enableLineButton: flag,
                    enableLineButtonTime: date,
                    useStartTime: date,
                    useStatus: true
                  }
                });
              }}
            />
          )
        );
      default:
        return false;
    }
  }, [state]);

  return (
    <>
      {state === 'loading' ? (
        <></>
      ) : disclaimerComponent ? (
        <NoneLayout
          page={disclaimerComponent}
          showGreeting
          hideUserName
          showStripedBackground
          disableMainLink
        />
      ) : (
        children
      )}
    </>
  );
};
